import { motion } from 'framer-motion'
import { Trans, useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight } from '@phosphor-icons/react'
import ShimmerButton from '../magicui/ShimmerButton'
import BrandLogo from '../website/BrandLogo'
import BrandLockup from '../betternotes/BrandLockup'

/** Body / kicker slots — wordmarks render at body scale. */
const BODY_BRAND_SLOTS = {
  brand: <BrandLogo variant="hero" className="inline-flex align-baseline text-white light:text-gray-900" />,
  notes: <BrandLockup size="md" className="inline-flex align-baseline" />,
} as const

/** Title slots — wordmarks need to sit at title scale, not body scale.
 *  GPT-5.5 2026-04-28 fix for the typography mismatch Mustapha flagged
 *  ("better/notes est dans ta navbar." had the wordmark visibly smaller
 *  than the surrounding sentence). Title slots upsize the lockup so it
 *  reads at the same weight as the rest of the title line. */
const TITLE_BRAND_SLOTS = {
  brand: <BrandLogo variant="hero" className="inline-flex align-[-0.08em] text-white light:text-gray-900" />,
  notes: <BrandLockup size="lg" className="inline-flex align-[-0.08em]" />,
} as const

/**
 * TourCard — the floating explainer card that follows each tour step.
 *
 * Two modes:
 *   - `fullscreen`: rendered dead-centre via a flex wrapper; used on
 *     welcome + final steps where the moment deserves the stage. The
 *     card is wider, typography is editorial, and a BrandLogo lockup
 *     sits at the top to frame the welcome with respect.
 *   - `anchored`: positioned relative to the spotlight rect, auto-picks
 *     above/below based on available viewport space.
 *
 * Centering fix (2026-04-18): previously we positioned the fullscreen
 * card with `top/left: 50%; transform: translate(-50%, -50%)`. Framer
 * Motion's own animate transform overrides that inline `transform`,
 * which made the card land in the bottom-right corner of the viewport
 * once the animation settled. Now we wrap fullscreen content in a
 * `fixed inset-0 flex items-center justify-center` layer — centring
 * comes from flex, the motion element keeps its own transform for
 * entrance/exit.
 */
export type TourCardPlacement = 'fullscreen' | 'anchored'

interface TourCardProps {
  placement: TourCardPlacement
  /** Anchored target rect — only used when placement === 'anchored'. */
  rect?: DOMRect | null
  /** i18n key (in `tour` namespace) for the optional small kicker above the title. Empty string hides it. */
  kickerKey?: string
  /** i18n key (in `tour` namespace) for the main title. Rendered with `<Trans>` so `<brand/>` / `<notes/>` slots interpolate. */
  titleKey: string
  /** i18n key (in `tour` namespace) for the body copy. Same `<Trans>` slot support as title. */
  bodyKey: string
  current: number
  total: number
  stepOfLabel: string
  primaryLabel: string
  backLabel: string
  showBack: boolean
  isFinal: boolean
  onPrimary: () => void
  onBack: () => void
}

const ANCHORED_WIDTH = 400
const FULLSCREEN_WIDTH = 520
const CARD_GAP = 16
const VIEWPORT_GUTTER = 16
const MOBILE_SHEET_GUTTER = 12
const ANCHORED_SAFE_HEIGHT = 300

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max))
}

export default function TourCard({
  placement,
  rect,
  kickerKey,
  titleKey,
  bodyKey,
  current,
  total,
  stepOfLabel,
  primaryLabel,
  backLabel,
  showBack,
  isFinal,
  onPrimary,
  onBack,
}: TourCardProps) {
  const { t } = useTranslation('tour')
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1280
  const vh = typeof window !== 'undefined' ? window.innerHeight : 720
  const isFullscreen = placement === 'fullscreen' || !rect

  // Optional founder signature block — opt-in per step via i18n keys
  // `<stepId>.signature_name` and `<stepId>.signature_role`. Currently
  // only the final "go" step uses it. Step id is reverse-engineered
  // from the title key (`<stepId>.title`) so we don't need to thread
  // an extra prop through FirstLoginTour.
  const stepId = titleKey.split('.')[0]
  const sigName = t(`${stepId}.signature_name`, { defaultValue: '' })
  const sigRoleKey = `${stepId}.signature_role`
  const sigRoleResolved = t(sigRoleKey, { defaultValue: '' })
  const hasSignature = sigName.length > 0
  const hasSigRole = sigRoleResolved.length > 0

  const cardContent = (
    <div
      // Wave 8d.2.2 — BorderBeam ("snake lumineux") removed globally
      // per Mustapha. Card relies on neutral elevation (--bn-cta-shadow)
      // alone for premium lift, no rotating gradient frame.
      className={`relative isolate rounded-[28px] border border-[var(--bn-app-border)] bg-[#15151a] light:bg-white shadow-[0_20px_56px_-12px_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.04)] light:shadow-[0_14px_42px_-12px_rgba(9,9,11,0.12),inset_0_0_0_1px_rgba(0,0,0,0.03)] ${
        isFullscreen
          ? 'overflow-hidden px-10 py-12 text-center md:px-14 md:py-16'
          : 'max-h-[calc(100dvh-32px)] overflow-y-auto overscroll-contain p-6'
      }`}
      style={{
        willChange: 'transform, opacity',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* Fullscreen-only decorative layers — subtle radial mauve glow
          + hairline highlight top edge. Skipped on anchored steps to
          keep them compact and noise-free per Mustapha 2026-04-27. */}
      {isFullscreen && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-[55%] opacity-60 light:opacity-30"
            style={{
              background:
                'radial-gradient(ellipse at 50% 0%, rgba(167, 139, 250, 0.18) 0%, rgba(167, 139, 250, 0.04) 38%, transparent 72%)',
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-0 h-px"
            style={{
              background:
                'linear-gradient(90deg, transparent 0%, rgba(167, 139, 250, 0.35) 50%, transparent 100%)',
            }}
          />
        </>
      )}

      {/* Wave 9 Sujet 2.7 — X close button removed: tour is mandatory.
          User must click "Suivant" / "Retour" to navigate. onSkip prop
          + secondaryLabel kept in the interface for API stability and
          future re-introduction (e.g. an "I already know my way" link
          on welcome step). */}

      {/* Fullscreen welcome: a centred BrandLogo hero frames the moment
          like a title card — the user sees the brand first, then the
          message. Anchored steps keep the compact inline kicker row. */}
      {isFullscreen ? (
        <>
          <div className="relative mb-10 flex justify-center">
            <BrandLogo
              variant="hero"
              className="text-[28px] tracking-[-0.025em] text-white light:text-gray-900 md:text-[32px]"
            />
          </div>
          <h2
            id="tour-card-title"
            className="relative mx-auto max-w-[380px] text-center text-[26px] font-extralight leading-[1.1] tracking-[-0.025em] text-white light:text-gray-900 md:text-[30px]"
          >
            <Trans i18nKey={titleKey} ns="tour" components={TITLE_BRAND_SLOTS} />
          </h2>
          <p className="relative mx-auto mt-5 max-w-[320px] text-center text-[14.5px] font-light leading-[1.6] tracking-[-0.005em] text-[var(--bn-text-muted)] light:text-gray-500 md:text-[15px]">
            <Trans i18nKey={bodyKey} ns="tour" components={BODY_BRAND_SLOTS} />
          </p>
          {/* Step dots — refined: active = wide mauve-soft pill with
              soft glow halo, past = small mauve-soft 50%, future =
              hairline neutral. Carries progress without verbose
              caption. */}
          <div className="relative mt-12 flex items-center justify-center gap-1.5">
            {Array.from({ length: total }, (_, i) => {
              const isActive = i === current - 1
              const isPast = i < current - 1
              return (
                <span
                  key={i}
                  aria-hidden="true"
                  className={
                    isActive
                      ? 'h-1 w-7 rounded-full bg-[var(--bn-accent-soft)] shadow-[0_0_8px_rgba(167,139,250,0.45)] transition-all duration-300'
                      : isPast
                        ? 'h-1 w-1 rounded-full bg-[var(--bn-accent-soft)]/50 transition-all duration-300'
                        : 'h-1 w-1 rounded-full bg-white/12 light:bg-gray-300 transition-all duration-300'
                  }
                />
              )
            })}
          </div>
          <div className="relative mt-10 flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={onPrimary}
              className="group relative inline-flex h-[50px] min-w-[200px] items-center justify-center gap-2 overflow-hidden rounded-[14px] bg-[var(--bn-accent)] px-7 text-[14px] font-medium tracking-[-0.01em] text-white shadow-[0_10px_28px_-6px_rgba(109,40,217,0.55),inset_0_1px_0_rgba(255,255,255,0.22)] transition-all duration-200 hover:bg-[var(--bn-accent-hover)] hover:-translate-y-px hover:shadow-[0_14px_36px_-6px_rgba(109,40,217,0.7),inset_0_1px_0_rgba(255,255,255,0.25)] active:translate-y-0 focus-visible:outline-none focus-visible:[box-shadow:var(--bn-focus-ring)]"
            >
              <span className="relative z-[2]">{primaryLabel}</span>
              <ArrowRight
                weight="bold"
                className="relative z-[2] h-[15px] w-[15px] transition-transform duration-200 group-hover:translate-x-0.5"
              />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 -left-[150%] h-full w-[55%]"
                style={{
                  background:
                    'linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                  transform: 'skewX(-20deg)',
                  animation: 'bc-cta-shimmer 3.5s ease-in-out infinite',
                }}
              />
            </button>
            {showBack && (
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium tracking-[-0.005em] text-[var(--bn-text-subtle)] transition-colors duration-150 hover:text-zinc-300 light:hover:text-gray-600"
              >
                <ArrowLeft className="h-3 w-3" weight="bold" />
                {backLabel}
              </button>
            )}
          </div>
          {/* Founder signature — last step only. Hairline gradient
              divider + medium display name + italic light role with
              brand slot interpolation for the slash mauve. */}
          {hasSignature && (
            <div className="relative mt-12 flex flex-col items-center">
              <div
                aria-hidden="true"
                className="mb-5 h-px w-20 light:hidden"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                }}
              />
              <div
                aria-hidden="true"
                className="mb-5 hidden h-px w-20 light:block"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(9,9,11,0.16) 50%, transparent 100%)',
                }}
              />
              <p className="text-[13.5px] font-medium tracking-[-0.015em] text-white/95 light:text-gray-900">
                {sigName}
              </p>
              {hasSigRole && (
                <p className="mt-1 text-[11.5px] italic font-light tracking-[-0.005em] text-[var(--bn-text-subtle)] light:text-gray-500">
                  <Trans i18nKey={sigRoleKey} ns="tour" components={BODY_BRAND_SLOTS} />
                </p>
              )}
            </div>
          )}
          {/* Screen-reader-only step label so the tour remains
              navigable without the caption being visible. */}
          <span className="sr-only">{stepOfLabel}</span>
        </>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.18em] text-[var(--bn-accent)]">
            {kickerKey && (
              <span>
                <Trans i18nKey={kickerKey} ns="tour" components={BODY_BRAND_SLOTS} />
              </span>
            )}
            {kickerKey && <span className="text-zinc-600 light:text-gray-300">·</span>}
            <span className="text-[var(--bn-text-subtle)] light:text-[var(--bn-text-muted)]">{stepOfLabel}</span>
          </div>

          <h2
            id="tour-card-title"
            className="mt-3 text-xl font-light leading-tight tracking-tight text-white light:text-gray-900 md:text-2xl"
          >
            <Trans i18nKey={titleKey} ns="tour" components={TITLE_BRAND_SLOTS} />
          </h2>

          <p className="mt-3 text-sm font-light leading-relaxed text-[var(--bn-text-muted)] light:text-gray-600">
            <Trans i18nKey={bodyKey} ns="tour" components={BODY_BRAND_SLOTS} />
          </p>

          <div className="mt-5 flex items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-1.5">
              {Array.from({ length: total }).map((_, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  className={
                    i + 1 === current
                      ? 'h-1.5 w-5 rounded-full bg-[var(--bn-accent)] transition-colors duration-200'
                      : i + 1 < current
                        ? 'h-1.5 w-1.5 rounded-full bg-[var(--bn-accent-soft)]/70 transition-colors duration-200'
                        : 'h-1.5 w-1.5 rounded-full bg-white/15 light:bg-gray-300 transition-colors duration-200'
                  }
                />
              ))}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {showBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-medium text-[var(--bn-text-muted)] transition-colors hover:bg-white/5 hover:text-zinc-200 light:text-[var(--bn-text-subtle)] light:hover:bg-gray-100 light:hover:text-gray-800"
                >
                  <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
                  {backLabel}
                </button>
              )}
              {isFinal ? (
                <ShimmerButton
                  type="button"
                  onClick={onPrimary}
                  className="!py-2.5 !px-4 shrink-0 whitespace-nowrap text-xs"
                >
                  {primaryLabel}
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" weight="bold" />
                </ShimmerButton>
              ) : (
                <button
                  type="button"
                  onClick={onPrimary}
                  // Direction A: primary "Suivant" CTA — neutral
                  // elevation (--bn-cta-shadow) on --bn-accent fill,
                  // hover bumps to --bn-accent-hover (#5B21B6).
                  // Radius 10px (action), font-weight 620.
                  style={{ fontWeight: 'var(--bn-cta-font-weight)' }}
                  className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[var(--bn-radius-action)] bg-[var(--bn-accent)] hover:bg-[var(--bn-accent-hover)] px-4 py-2 text-xs text-white shadow-[var(--bn-cta-shadow)] hover:shadow-[var(--bn-cta-shadow-hover)] hover:-translate-y-px active:translate-y-0 transition-all duration-150"
                >
                  {primaryLabel}
                  <ArrowRight className="h-3.5 w-3.5 shrink-0" weight="bold" />
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )

  // Fullscreen: flex-centered wrapper. Framer's animate-transform no
  // longer fights with a static `translate(-50%, -50%)`.
  if (isFullscreen) {
    return (
      <div className="pointer-events-none fixed inset-0 z-[90] flex items-center justify-center px-4">
        <motion.div
          key={`card-${current}`}
          initial={{ opacity: 0, y: 12, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="tour-card-title"
          style={{ width: Math.min(FULLSCREEN_WIDTH, vw - 32), pointerEvents: 'auto' }}
        >
          {cardContent}
        </motion.div>
      </div>
    )
  }

  // Anchored: computed top/left per target rect.
  const r = rect as DOMRect

  // Mobile (< sm breakpoint): bottom sheet, always. Anchored tooltips on
  // phones are fake precision; they collide with tabs, nav, and thumbs.
  // GPT-5.5 2026-04-28 design pass: drop the opposite-half logic, lock
  // the card to the bottom with safe-area-inset-bottom for notch/home
  // indicator clearance.
  if (vw < 640) {
    return (
      <motion.div
        key={`card-${current}`}
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 10, scale: 0.98 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-card-title"
        style={{
          position: 'fixed',
          left: MOBILE_SHEET_GUTTER,
          right: MOBILE_SHEET_GUTTER,
          bottom: `calc(${MOBILE_SHEET_GUTTER}px + env(safe-area-inset-bottom))`,
          maxWidth: vw - MOBILE_SHEET_GUTTER * 2,
        }}
        className="z-[90]"
      >
        {cardContent}
      </motion.div>
    )
  }

  // Desktop / tablet anchored placement. GPT-5.5 2026-04-28 redesign:
  // try right of target → fall back to left → fall back to centered
  // above/below. Always clamp inside viewport with VIEWPORT_GUTTER.
  // No more translateY(-100%) catastrophe when target is near top/bottom
  // edges (the old code happily placed cards off-screen above viewport).
  const width = Math.min(ANCHORED_WIDTH, vw - VIEWPORT_GUTTER * 2)
  const viewportHeightBudget = Math.max(180, vh - VIEWPORT_GUTTER * 2)
  const safeHeight = Math.min(ANCHORED_SAFE_HEIGHT, viewportHeightBudget)
  const targetCenterX = r.left + r.width / 2
  const targetCenterY = r.top + r.height / 2
  const roomRight = vw - r.right - CARD_GAP - VIEWPORT_GUTTER
  const roomLeft = r.left - CARD_GAP - VIEWPORT_GUTTER
  const canPlaceRight = roomRight >= width
  const canPlaceLeft = roomLeft >= width

  let left: number
  let top: number

  if (canPlaceRight) {
    left = r.right + CARD_GAP
    top = targetCenterY - safeHeight / 2
  } else if (canPlaceLeft) {
    left = r.left - CARD_GAP - width
    top = targetCenterY - safeHeight / 2
  } else {
    left = targetCenterX - width / 2
    const spaceBelow = vh - r.bottom - CARD_GAP - VIEWPORT_GUTTER
    const spaceAbove = r.top - CARD_GAP - VIEWPORT_GUTTER
    const placeBelow = spaceBelow >= safeHeight || spaceBelow >= spaceAbove
    top = placeBelow ? r.bottom + CARD_GAP : r.top - CARD_GAP - safeHeight
  }

  left = clamp(left, VIEWPORT_GUTTER, vw - width - VIEWPORT_GUTTER)
  top = clamp(top, VIEWPORT_GUTTER, vh - safeHeight - VIEWPORT_GUTTER)
  const maxHeight = Math.max(180, vh - top - VIEWPORT_GUTTER)

  return (
    <motion.div
      key={`card-${current}`}
      initial={{ opacity: 0, y: 8, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 8, scale: 0.98 }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-card-title"
      style={{
        position: 'fixed',
        top,
        left,
        width,
        maxHeight,
      }}
      className="z-[90]"
    >
      {cardContent}
    </motion.div>
  )
}
